/** BuildupView — 체크업북 STEP 1~4 순차 진행 + STEP 4 녹음. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { motion, useReducedMotion } from 'framer-motion'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'
import step1Image from '@/assets/learner/buildup/step-1.png'
import step2Image from '@/assets/learner/buildup/step-2.png'
import step3Image from '@/assets/learner/buildup/step-3.png'
import step4Image from '@/assets/learner/buildup/step-4.png'
import {
  completeSession,
  getAudioUploadUrl,
  saveBuildupAudioResponse,
  selfGradeBuildupStep,
  uploadAudioToS3,
  type BuildupLearnerGrade,
  type LcBuildupDataResponse,
  type LcBuildupSelfGradeItem,
  type LcBuildupSelfGradeStep,
} from '@/api/lcLearning'
import { getApiErrorMessage } from '@/utils/apiError'
import WarmupAudioRecorder from './WarmupAudioRecorder'
import { Button } from './ui/Button'
import { Button3D } from './ui/Button3D'
import { Card } from './ui/Card'
import { Mascot } from './ui/Mascot'
import { resolveInitialPhase, type BuildupPhase } from './buildup/resolveInitialPhase'
import {
  readBuildupPhase,
  writeBuildupPhase,
} from './buildup/buildupPhaseStorage'
import type { RuntimeMode } from './ComponentRunner'

// 자가채점 phase: STEP 4 완료 후 selfGradingEnabled=true 면 진입한다.
type ExtendedBuildupPhase = BuildupPhase | 'self-grade'
type BuildupStep = 1 | 2 | 3 | 4

const STEP_IMAGES: Record<BuildupStep, string> = {
  1: step1Image,
  2: step2Image,
  3: step3Image,
  4: step4Image,
}

const STEP_ACCENTS: Record<BuildupStep, { badge: string; button: string }> = {
  1: { badge: 'bg-[#ed6d34]', button: 'bg-[#ed6d34] hover:bg-[#d95f2b]' },
  2: { badge: 'bg-[#00ada9]', button: 'bg-[#00ada9] hover:bg-[#009a96]' },
  3: { badge: 'bg-lc-key-500', button: 'bg-lc-key-500 hover:bg-lc-key-600' },
  4: { badge: 'bg-[#d95097]', button: 'bg-[#d95097] hover:bg-[#c24286]' },
}

const BUILDUP_STEP_PATTERN = /^STEP\s+(\d+(?:-\d+)?)$/i
const BUILDUP_STEP_TITLE_PATTERN = /^\s*\[([^\]]+)]/
const BUILDUP_CARD_TRANSITION = {
  type: 'spring',
  duration: 0.511,
  bounce: 0.15,
} as const
const BUILDUP_CONTENT_TRANSITION = {
  duration: 0.511,
  ease: [0.22, 0.8, 0.36, 1],
} as const
const BUILDUP_CONTENT_OFFSET = 20
const REDUCED_MOTION_TRANSITION = { duration: 0 } as const

function getBuildupStepNumber(step: string): string | null {
  return BUILDUP_STEP_PATTERN.exec(step.trim())?.[1] ?? null
}

function getBuildupStepTitle(title: string): string {
  return BUILDUP_STEP_TITLE_PATTERN.exec(title)?.[1]?.trim() || title
}

interface Props {
  componentProgressId: number | null
  enrollmentId: number
  componentType: string
  label: string
  buildupData: LcBuildupDataResponse
  runtimeMode?: RuntimeMode
  /** 교사 지정 복습 레슨 여부 — Task 7 에서 book-skip 로직 구현 예정. */
  isDesignatedReview?: boolean
  onComplete: () => void
}

export default function BuildupView({
  componentProgressId,
  enrollmentId,
  componentType,
  label: _label,
  buildupData,
  runtimeMode = 'live',
  isDesignatedReview,
  onComplete,
}: Props) {
  const { t } = useTranslation('learner')
  const isLive = runtimeMode === 'live'

  const [phase, setPhase] = useState<ExtendedBuildupPhase>(() =>
    resolveInitialPhase({
      stored: readBuildupPhase(componentProgressId),
      audioResponse1: buildupData.audioResponse1,
      audioResponse2: buildupData.audioResponse2,
      isDesignatedReview,
    }),
  )
  const [bookStep, setBookStep] = useState<BuildupStep>(1)
  const legacyAudioUrl = buildupData.audioResponse1?.startsWith('https://')
    ? buildupData.audioResponse1
    : null
  const existingAudioUrl = buildupData.audioResponse2?.trim() || legacyAudioUrl
  const [audioSubmitted, setAudioSubmitted] = useState(Boolean(existingAudioUrl))
  const legacyExplainText =
    buildupData.audioResponse1 &&
    !buildupData.audioResponse1.startsWith('https://')
      ? buildupData.audioResponse1
      : ''
  const [recordingSkipped, setRecordingSkipped] = useState(
    legacyExplainText.trim().length > 0,
  )
  const canComplete = audioSubmitted || recordingSkipped

  const [error, setError] = useState<string | null>(null)
  const [completing, setCompleting] = useState(false)

  useEffect(() => {
    if (!isLive) return
    // 자가채점은 BuildupPhase 타입이 아니므로 저장하지 않는다.
    if (phase === 'p1' || phase === 'p2') {
      writeBuildupPhase(componentProgressId, phase)
    }
  }, [phase, componentProgressId, isLive])

  const advanceBookStep = useCallback(() => {
    setError(null)
    setBookStep((current) => {
      if (current < 3) return (current + 1) as BuildupStep
      setPhase('p2')
      return 3
    })
  }, [])

  const handleAudioSubmit = useCallback(
    async (blob: Blob) => {
      if (isLive && componentProgressId != null) {
        const { uploadUrl, fileUrl } = await getAudioUploadUrl(componentProgressId, 2)
        await uploadAudioToS3(uploadUrl, blob)
        await saveBuildupAudioResponse(componentProgressId, 2, fileUrl)
      }
      setAudioSubmitted(true)
    },
    [isLive, componentProgressId],
  )

  const handleComplete = useCallback(async () => {
    if (!canComplete || completing) return
    setError(null)
    setCompleting(true)
    try {
      if (isLive && componentProgressId != null) {
        await completeSession(componentProgressId, { enrollmentId, componentType })
      }
      // selfGradingEnabled=true 면 자가채점으로 진입, 아니면 완료
      if (buildupData.selfGradingEnabled && buildupData.selfGradeSteps.length > 0) {
        setCompleting(false)
        setPhase('self-grade')
      } else {
        onComplete()
      }
    } catch {
      setError(t('buildup.errors.complete'))
      setCompleting(false)
    }
  }, [
    completing,
    canComplete,
    isLive,
    componentProgressId,
    enrollmentId,
    componentType,
    buildupData,
    onComplete,
    t,
  ])

  return (
    <div className="flex min-h-full flex-col gap-5">
      <div
        key={phase === 'self-grade' ? 'self-grade' : 'steps'}
        className="flex min-h-0 flex-1 flex-col animate-buildup-phase motion-reduce:animate-none"
      >
        {phase === 'p1' || phase === 'p2' ? (
          <BuildupSteps
            buildupData={buildupData}
            currentStep={phase === 'p2' ? 4 : bookStep}
            canComplete={canComplete}
            existingAudioUrl={existingAudioUrl}
            completing={completing}
            recordingSkipped={recordingSkipped}
            onAdvance={advanceBookStep}
            onSubmitAudio={handleAudioSubmit}
            onSkipRecording={() => setRecordingSkipped(true)}
            onComplete={handleComplete}
          />
        ) : (
          <PhaseThree
            componentProgressId={componentProgressId}
            selfGradeSteps={buildupData.selfGradeSteps}
            isLive={isLive}
            onComplete={onComplete}
          />
        )}
      </div>

      {error && (
        <Card size="md" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-lc-red-900 text-white flex items-center justify-center text-[18px] font-bold shrink-0">
            ✕
          </div>
          <p className="text-[16px] leading-[1.5] tracking-[-0.6px] text-lc-red-dark">{error}</p>
        </Card>
      )}

    </div>
  )
}

function BuildupSteps({
  buildupData,
  currentStep,
  canComplete,
  existingAudioUrl,
  completing,
  recordingSkipped,
  onAdvance,
  onSubmitAudio,
  onSkipRecording,
  onComplete,
}: {
  buildupData: LcBuildupDataResponse
  currentStep: BuildupStep
  canComplete: boolean
  existingAudioUrl: string | null
  completing: boolean
  recordingSkipped: boolean
  onAdvance: () => void
  onSubmitAudio: (blob: Blob) => Promise<void>
  onSkipRecording: () => void
  onComplete: () => void
}) {
  const { t } = useTranslation('learner')
  const shouldReduceMotion = useReducedMotion()
  const stepListRef = useRef<HTMLOListElement>(null)
  const [selectedStep, setSelectedStep] = useState<BuildupStep>(currentStep)

  useEffect(() => {
    setSelectedStep(currentStep)
  }, [currentStep])

  useEffect(() => {
    const selectedCard = stepListRef.current?.querySelector<HTMLElement>('[data-open="true"]')
    selectedCard?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'end',
    })
  }, [selectedStep])

  const titleFor = (step: BuildupStep) => {
    const configured = buildupData.stepTitles.find(
      (item) => getBuildupStepNumber(item.step) === String(step),
    )?.title
    return configured
      ? getBuildupStepTitle(configured)
      : t(`buildup.steps.${step}.title`)
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-5" aria-labelledby="buildup-title">
      <div className="flex shrink-0 flex-col gap-2 px-2">
        <h2 id="buildup-title" className="text-lc-v3-h3 font-bold text-lc-text-primary">
          {t('buildup.title')}
        </h2>
        <p className="text-lc-v3-h5 font-medium text-lc-text-secondary">
          {t('buildup.guide')}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-x-auto pb-2">
        <ol ref={stepListRef} className="flex h-[424px] min-w-[1072px] gap-4">
          {([1, 2, 3, 4] as const).map((step) => {
            const state = step < currentStep ? 'done' : step === currentStep ? 'current' : 'future'
            const isCurrent = state === 'current'
            const isFuture = state === 'future'
            const isOpen = step === selectedStep
            const accent = STEP_ACCENTS[step]
            const stepTitle = titleFor(step)
            const contentOffset =
              shouldReduceMotion || isOpen
                ? 0
                : step < selectedStep
                  ? -BUILDUP_CONTENT_OFFSET
                  : BUILDUP_CONTENT_OFFSET

            const handleOpenCardDone = () => {
              if (isCurrent) {
                onAdvance()
              } else {
                setSelectedStep(currentStep)
              }
            }

            return (
              <motion.li
                key={step}
                data-step={step}
                data-state={state}
                data-open={isOpen}
                aria-current={isCurrent ? 'step' : undefined}
                aria-disabled={isFuture || undefined}
                initial={false}
                animate={{ flexGrow: isOpen ? 1 : 0 }}
                transition={
                  shouldReduceMotion
                    ? REDUCED_MOTION_TRANSITION
                    : BUILDUP_CARD_TRANSITION
                }
                className={`flex h-full basis-[168px] shrink-0 overflow-hidden rounded-[20px] transition-[background-color,box-shadow] duration-[511ms] ease-[cubic-bezier(.22,.8,.36,1)] motion-reduce:transition-none ${
                  isOpen
                    ? 'bg-lc-bg-primary shadow-lc-v3-card'
                    : isFuture
                      ? 'bg-lc-bg-tertiary'
                      : 'bg-lc-bg-primary shadow-lc-v3-card'
                }`}
              >
                {isOpen && step === 4 ? (
                  <div className="flex min-w-0 flex-1 animate-buildup-card-content items-start justify-center gap-10 py-10 pl-8 pr-10 motion-reduce:animate-none">
                    <div className="flex w-40 shrink-0 flex-col items-center gap-8 text-center">
                      <StepHeader
                        step={step}
                        title={stepTitle}
                        badgeClass={accent.badge}
                      />
                      <p className="text-lc-v3-body-1 font-medium text-lc-text-secondary">
                        {t(`buildup.steps.${step}.description`)}
                      </p>
                      <img src={STEP_IMAGES[step]} alt="" className="size-20 object-contain" />
                      <Button
                        size="L"
                        disabled={!canComplete || completing}
                        onClick={onComplete}
                        className={`${accent.button} disabled:bg-lc-text-disabled disabled:text-white`}
                      >
                        {t('buildup.stepDone')}
                      </Button>
                    </div>
                    <div className="flex h-full min-w-[268px] flex-1 overflow-y-auto rounded-2xl bg-lc-disabled-bg px-5 py-2">
                      <div className="flex min-h-0 w-full flex-col">
                        <WarmupAudioRecorder
                          onSubmit={onSubmitAudio}
                          existingUrl={existingAudioUrl}
                          maxDurationSec={180}
                          unavailableActionLabel={t('buildup.step4.skipRecording')}
                          onUnavailableAction={recordingSkipped ? undefined : onSkipRecording}
                        />
                        {recordingSkipped ? (
                          <p className="mx-auto mb-5 rounded-xl bg-white px-4 py-3 text-center text-lc-v3-body-2 font-medium text-lc-text-secondary shadow-sm">
                            {t('buildup.step4.recordingSkipped')}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative flex h-full w-full flex-col items-center px-4 py-10 text-center">
                    <StepHeader
                      step={step}
                      title={stepTitle}
                      badgeClass={isFuture ? 'bg-lc-text-disabled' : accent.badge}
                      disabled={isFuture}
                    />

                    <div className="pointer-events-none absolute inset-x-0 top-[135px] flex justify-center">
                      <motion.p
                        aria-hidden={!isOpen}
                        initial={false}
                        animate={{ opacity: isOpen ? 1 : 0, x: contentOffset }}
                        transition={
                          shouldReduceMotion
                            ? REDUCED_MOTION_TRANSITION
                            : BUILDUP_CONTENT_TRANSITION
                        }
                        className="w-[280px] shrink-0 whitespace-pre-line text-lc-v3-body-1 font-medium text-lc-text-secondary"
                      >
                        {t(`buildup.steps.${step}.description`)}
                      </motion.p>
                    </div>

                    <img
                      src={STEP_IMAGES[step]}
                      alt=""
                      className={`absolute left-1/2 top-[227px] size-20 -translate-x-1/2 object-contain transition-[filter,opacity] duration-[511ms] ease-[cubic-bezier(.22,.8,.36,1)] motion-reduce:transition-none ${
                        isFuture ? 'opacity-40 grayscale' : ''
                      }`}
                    />

                    <div
                      className={`absolute inset-x-0 top-[331px] flex justify-center ${
                        isOpen ? '' : 'pointer-events-none'
                      }`}
                    >
                      <motion.div
                        aria-hidden={!isOpen}
                        initial={false}
                        animate={{ opacity: isOpen ? 1 : 0, x: contentOffset }}
                        transition={
                          shouldReduceMotion
                            ? REDUCED_MOTION_TRANSITION
                            : BUILDUP_CONTENT_TRANSITION
                        }
                      >
                        <Button
                          size="L"
                          onClick={handleOpenCardDone}
                          tabIndex={isOpen ? 0 : -1}
                          className={accent.button}
                        >
                          {t('buildup.stepDone')}
                        </Button>
                      </motion.div>
                    </div>

                    {!isOpen && !isFuture ? (
                      <button
                        type="button"
                        onClick={() => setSelectedStep(step)}
                        aria-label={t('buildup.reopenStep', { n: step, title: stepTitle })}
                        aria-expanded={false}
                        className="absolute inset-0 rounded-[20px] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-lc-key-500"
                      />
                    ) : null}
                  </div>
                )}
              </motion.li>
            )
          })}
        </ol>
      </div>

      <div className="ml-auto flex w-full max-w-40 shrink-0 flex-col items-center pt-3">
        <Button
          variant={canComplete ? 'navy' : 'neutral'}
          size="XL"
          disabled={!canComplete || completing}
          onClick={onComplete}
          className="w-full disabled:bg-lc-text-disabled disabled:text-white"
        >
          {completing ? t('buildup.completing') : t('buildup.completeLearning')}
        </Button>
      </div>
    </section>
  )
}

function StepHeader({
  step,
  title,
  badgeClass,
  disabled = false,
}: {
  step: BuildupStep
  title: string
  badgeClass: string
  disabled?: boolean
}) {
  const { t } = useTranslation('learner')
  return (
    <div className="flex shrink-0 flex-col items-center gap-2">
      <span
        className={`inline-flex rounded-full px-3 py-1.5 text-lc-v3-label-md font-semibold text-white transition-colors duration-[511ms] ease-[cubic-bezier(.22,.8,.36,1)] motion-reduce:transition-none ${badgeClass}`}
      >
        {t('buildup.stepNumber', { n: step })}
      </span>
      <h3
        className={`whitespace-nowrap text-lc-v3-h4 font-bold transition-colors duration-[511ms] ease-[cubic-bezier(.22,.8,.36,1)] motion-reduce:transition-none ${
          disabled ? 'text-lc-text-disabled' : 'text-lc-text-primary'
        }`}
      >
        <LearningContentText text={title} />
      </h3>
    </div>
  )
}

// ============================================================================
// Phase 3 — 자가채점 (selfGradingEnabled=true 일 때만 노출)
// ============================================================================

function PhaseThree({
  componentProgressId,
  selfGradeSteps,
  isLive,
  onComplete,
}: {
  componentProgressId: number | null
  selfGradeSteps: LcBuildupSelfGradeStep[]
  isLive: boolean
  onComplete: () => void
}) {
  const { t } = useTranslation('learner')
  return (
    <div className="flex flex-col gap-5">
      <Card size="md" className="flex items-start gap-3 bg-lc-lightblue-100">
        <Mascot name="ddami" shape="roundbox" size={48} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[18px] leading-[1.5] tracking-[-0.6px] font-semibold text-lc-gray-900 mb-1">
            {t('buildup.selfGrade.sectionTitle')}
          </p>
          <p className="text-[15px] leading-[1.5] text-lc-gray-700">
            {t('buildup.selfGrade.sectionHint')}
          </p>
        </div>
      </Card>

      {selfGradeSteps.map((step) => {
        const stepNumber = getBuildupStepNumber(step.step)
        return (
          <div key={step.step} className="flex flex-col gap-3">
            <h3 className="text-[16px] font-semibold tracking-[-0.6px] text-lc-gray-800">
              {stepNumber ? t('buildup.stepNumber', { n: stepNumber }) : step.step} —{' '}
              <LearningContentText text={step.title} />
            </h3>
            {step.items.map((item) => (
              <SelfGradeItemCard
                key={item.number}
                item={item}
                stepKey={step.step}
                componentProgressId={componentProgressId}
                isLive={isLive}
              />
            ))}
          </div>
        )
      })}

      <div className="flex justify-end pt-2">
        <Button3D variant="orange" size="L" onClick={onComplete}>
          {t('buildup.confirmComplete')}
        </Button3D>
      </div>
    </div>
  )
}

function SelfGradeItemCard({
  item,
  stepKey,
  componentProgressId,
  isLive,
}: {
  item: LcBuildupSelfGradeItem
  stepKey: string
  componentProgressId: number | null
  isLive: boolean
}) {
  const { t } = useTranslation('learner')
  const [showAnswer, setShowAnswer] = useState(false)
  const [localGrade, setLocalGrade] = useState<BuildupLearnerGrade | null>(item.grade)
  const [gradeError, setGradeError] = useState<string | null>(null)

  const isTeacherLocked = item.gradedBy === 'TEACHER'

  const mutation = useMutation({
    mutationFn: (grade: BuildupLearnerGrade) => {
      if (!isLive || componentProgressId == null) return Promise.resolve()
      return selfGradeBuildupStep(componentProgressId, {
        step: stepKey,
        itemNo: item.number,
        grade,
      })
    },
    onError: (e: unknown) => {
      setGradeError(getApiErrorMessage(e, t))
    },
  })

  const handleGradeChange = (grade: BuildupLearnerGrade) => {
    if (isTeacherLocked) return
    const prev = localGrade
    setGradeError(null)
    setLocalGrade(grade)
    // 실패(예: 교사 우선으로 거부 TEACHER_GRADE_LOCKED, 네트워크) 시 낙관값을 되돌려
    // 토글 표시가 서버 상태와 어긋나지 않게 한다.
    mutation.mutate(grade, { onError: () => setLocalGrade(prev) })
  }

  return (
    <Card size="md" className="flex flex-col gap-3">
      {/* 번호 + 제목 */}
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full bg-lc-lightblue-200 text-lc-gray-900 flex items-center justify-center text-[13px] font-semibold shrink-0">
          {item.number}
        </span>
        {item.title && (
          <p className="text-[15px] font-medium text-lc-gray-800">
            <LearningContentText text={item.title} />
          </p>
        )}
      </div>

      {/* 문제 */}
      {item.problem && (
        <div className="flex items-start gap-2 bg-lc-lightblue-50 p-3 rounded-lc-md">
          <Mascot name="queda" shape="circle" size={28} className="shrink-0" />
          <p className="flex-1 text-[15px] text-lc-gray-800">
            <LearningContentText text={item.problem} />
          </p>
        </div>
      )}

      {/* 보기 */}
      {item.options && (
        <p className="text-sm text-lc-gray-600 whitespace-pre-wrap">
          <LearningContentText text={item.options} />
        </p>
      )}

      {/* 문제 이미지 */}
      {item.probImageUrls.map((url) => (
        <img key={url} src={url} alt="" className="rounded-lc-md w-full" />
      ))}

      {/* 정답 확인 토글 */}
      <button
        type="button"
        onClick={() => setShowAnswer((v) => !v)}
        className="self-start text-[14px] font-medium text-lc-lightblue-900 underline underline-offset-2"
      >
        {showAnswer ? t('buildup.selfGrade.hideAnswer') : t('buildup.selfGrade.showAnswer')}
      </button>

      {showAnswer && (
        <div className="flex flex-col gap-2 bg-lc-gray-50 rounded-lc-md p-3 text-[14px] text-lc-gray-800">
          {item.answer && (
            <p>
              <span className="font-semibold">{t('buildup.selfGrade.answerLabel')}:</span>{' '}
              <LearningContentText text={item.answer} />
            </p>
          )}
          {item.answerCriteria && (
            <p>
              <span className="font-semibold">{t('buildup.selfGrade.criteriaLabel')}:</span>{' '}
              <LearningContentText text={item.answerCriteria} />
            </p>
          )}
          {item.desc && (
            <p>
              <span className="font-semibold">{t('buildup.selfGrade.descLabel')}:</span>{' '}
              <LearningContentText text={item.desc} />
            </p>
          )}
          {item.descImageUrls.map((url) => (
            <img key={url} src={url} alt="" className="rounded-lc-md w-full mt-1" />
          ))}
        </div>
      )}

      {/* 자가채점 토글 */}
      <div className="flex items-center gap-3 mt-1">
        {isTeacherLocked ? (
          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-lc-gray-500 bg-lc-gray-100 px-3 py-1 rounded-full">
            🔒 {t('buildup.selfGrade.teacherLocked')}
          </span>
        ) : (
          <LearnerGradeToggle
            value={localGrade}
            onChange={handleGradeChange}
            disabled={mutation.isPending}
          />
        )}
        {mutation.isSuccess && !gradeError && (
          <span className="text-[12px] text-lc-mint-dark">{t('buildup.selfGrade.saved')}</span>
        )}
      </div>

      {gradeError && (
        <p className="text-[13px] text-lc-red-dark">{gradeError}</p>
      )}
    </Card>
  )
}

// 학습자 전용 3-state 채점 토글 (coaching/BuildupStepGradeToggle 과 영역 분리)
const LEARNER_GRADE_OPTIONS: {
  grade: BuildupLearnerGrade
  testid: string
  symbol: string
  labelKey: string
  activeClass: string
}[] = [
  { grade: 'O', testid: 'learner-grade-o', symbol: 'O', labelKey: 'buildup.selfGrade.gradeO', activeClass: 'bg-green-600 text-white' },
  { grade: 'TRIANGLE', testid: 'learner-grade-triangle', symbol: '△', labelKey: 'buildup.selfGrade.gradeTriangle', activeClass: 'bg-amber-500 text-white' },
  { grade: 'X', testid: 'learner-grade-x', symbol: 'X', labelKey: 'buildup.selfGrade.gradeX', activeClass: 'bg-red-600 text-white' },
]

function LearnerGradeToggle({
  value,
  onChange,
  disabled,
}: {
  value: BuildupLearnerGrade | null
  onChange: (grade: BuildupLearnerGrade) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('learner')
  return (
    <div
      role="group"
      aria-label={t('buildup.selfGrade.gradeAriaLabel')}
      className="inline-flex rounded-md border border-lc-gray-200 overflow-hidden"
    >
      {LEARNER_GRADE_OPTIONS.map((opt, i) => (
        <button
          key={opt.grade}
          type="button"
          data-testid={opt.testid}
          disabled={disabled}
          aria-pressed={value === opt.grade}
          aria-label={t(opt.labelKey)}
          title={t(opt.labelKey)}
          onClick={() => onChange(opt.grade)}
          className={[
            'px-3 py-1.5 text-sm font-semibold leading-none',
            i > 0 ? 'border-l border-lc-gray-200' : '',
            value === opt.grade ? opt.activeClass : 'bg-white text-lc-gray-700',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ].filter(Boolean).join(' ')}
        >
          {opt.symbol}
        </button>
      ))}
    </div>
  )
}
